import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'

import { Link } from 'react-router-dom'

import { IconAlert, IconCheck, IconChevronLeft } from '@/components/icons'

/**
 * Ελάχιστα δομικά στοιχεία διεπαφής, mobile first.
 * Κάθε διαδραστικός στόχος τηρεί το ελάχιστο ύψος αφής των 44px (§11).
 */

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  loading?: boolean
}

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  children,
  className = '',
  ...props
}: ButtonProps) {
  const base =
    'inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 text-base font-semibold ' +
    'transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-60'

  const variants = {
    primary: 'bg-brand-700 text-white shadow-card hover:bg-brand-600 active:bg-brand-900',
    secondary: 'border border-gray-300 bg-white text-gray-800 hover:border-gray-400 hover:bg-gray-50',
    ghost: 'text-brand-700 hover:bg-brand-50',
    danger: 'border border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
  }

  return (
    <button
      {...props}
      disabled={disabled || loading}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {loading && (
        <span
          aria-hidden
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  )
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
  hint?: string
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, hint, id, className = '', ...props },
  ref,
) {
  const inputId = id ?? props.name
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
        {label}
      </label>
      <input
        {...props}
        id={inputId}
        ref={ref}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${inputId}-error` : undefined}
        className={
          'h-touch rounded-xl border px-3.5 text-base text-gray-900 outline-none transition-colors ' +
          'focus:border-brand-600 focus:ring-2 focus:ring-brand-100 ' +
          (error ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white') +
          ` ${className}`
        }
      />
      {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
      {error && (
        <p id={`${inputId}-error`} role="alert" className="text-xs font-medium text-red-600">
          {error}
        </p>
      )}
    </div>
  )
})

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'success' | 'warning'
  children: ReactNode
}) {
  const tones = {
    error: 'border-red-200 bg-red-50 text-red-800',
    success: 'border-brand-200 bg-brand-50 text-brand-900',
    warning: 'border-amber-200 bg-amber-50 text-amber-900',
  }
  const Icon = tone === 'success' ? IconCheck : IconAlert

  return (
    <div role="alert" className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm ${tones[tone]}`}>
      <Icon size={18} className="mt-0.5 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/**
 * Το βασικό δοχείο περιεχομένου.
 *
 * Η προαιρετική λωρίδα στην αριστερή ακμή κωδικοποιεί κατάσταση με χρώμα, ώστε
 * να διαβάζεται με μια ματιά χωρίς να χρειάζεται ανάγνωση του κειμένου.
 */
export function Card({
  accent,
  className = '',
  children,
  ...props
}: {
  accent?: 'brand' | 'amber' | 'red' | 'accent' | null
  className?: string
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>) {
  const accents = {
    brand: 'before:bg-brand-500',
    amber: 'before:bg-amber-400',
    red: 'before:bg-red-400',
    accent: 'before:bg-accent-500',
  }
  const stripe = accent
    ? `relative overflow-hidden before:absolute before:inset-y-0 before:left-0 before:w-1 ${accents[accent]}`
    : ''

  return (
    <div
      {...props}
      className={`rounded-2xl border border-gray-200/80 bg-white shadow-card ${stripe} ${className}`}
    >
      {children}
    </div>
  )
}

/** Επικεφαλίδα ενότητας. Ίδιο βάρος παντού, ώστε η σελίδα να έχει ρυθμό. */
export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-2 mt-5 flex items-center justify-between gap-3 first:mt-0">
      <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500">{children}</h2>
      {action}
    </div>
  )
}

/**
 * Κενή κατάσταση με εικονίδιο και ενέργεια.
 *
 * Μια γκρι πρόταση μέσα σε διακεκομμένο πλαίσιο μοιάζει με σφάλμα. Η κενή
 * οθόνη είναι η πρώτη που βλέπει ο νέος χρήστης και πρέπει να του λέει τι να
 * κάνει, όχι τι δεν υπάρχει.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200/80 bg-white px-6 py-10 text-center shadow-card">
      <span className="flex size-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
        {icon}
      </span>
      <p className="font-semibold text-gray-900">{title}</p>
      {description && <p className="max-w-xs text-sm text-gray-500">{description}</p>}
      {action && <div className="mt-1 w-full max-w-xs">{action}</div>}
    </div>
  )
}

/** Γκρι μπλοκ στο σχήμα του περιεχομένου που έρχεται. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`shimmer rounded-lg bg-gray-200/70 ${className}`} />
}

/**
 * Σκελετός λίστας.
 *
 * Προτιμάται από το «Φόρτωση…»: κρατά το ύψος της σελίδας σταθερό, οπότε το
 * περιεχόμενο δεν αναπηδά όταν φτάσει, και η αναμονή φαίνεται μικρότερη.
 */
export function SkeletonList({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2" aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-3 rounded-2xl border border-gray-200/80 bg-white p-4 shadow-card"
        >
          <Skeleton className="size-10 shrink-0 rounded-xl" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Επιστροφή στην προηγούμενη οθόνη.
 *
 * Ένα σκέτο «← Πίσω» σε μπλε κείμενο μοιάζει με ξεχασμένο σύνδεσμο. Ως
 * κουμπί με το δικό του περίγραμμα και 44px ύψους πατιέται με τον αντίχειρα
 * και φαίνεται ότι είναι ενέργεια.
 */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="mb-3 inline-flex h-10 items-center gap-1 rounded-xl border border-gray-200 bg-white pl-2 pr-3.5 text-sm font-semibold text-gray-700 shadow-card transition-colors hover:border-brand-300 hover:text-brand-700"
    >
      <IconChevronLeft size={18} />
      {label}
    </Link>
  )
}

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <main className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-6 px-5 py-10">
      <header className="text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-700">B2Vend</p>
        <h1 className="mt-2 text-2xl font-bold text-gray-900">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-gray-500">{subtitle}</p>}
      </header>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">{children}</section>

      {footer && <footer className="text-center text-sm text-gray-500">{footer}</footer>}
    </main>
  )
}
